import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { FileText, Plus, Edit, Trash2, ArrowLeftRight, User } from 'lucide-react';

const actionIcons = {
  create: Plus,
  update: Edit,
  delete: Trash2
};

const actionColors = {
  create: 'bg-[#34C759]/10 text-[#34C759]',
  update: 'bg-[#007AFF]/10 text-[#007AFF]',
  delete: 'bg-[#FF3B30]/10 text-[#FF3B30]'
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, [entityFilter]);

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (entityFilter && entityFilter !== 'all') params.append('entity_type', entityFilter);
      
      const response = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const getActionBadge = (action) => {
    const Icon = actionIcons[action] || Edit;
    const color = actionColors[action] || actionColors.update;
    return (
      <Badge className={color}>
        <Icon className="w-3 h-3 mr-1" />
        {action}
      </Badge>
    );
  };

  const entityTypes = ['user', 'organization', 'warehouse', 'item', 'stock_movement', 'subscription'];

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">AUDIT LOG</h1>
          <p className="text-[#0A0A0A]/60 mt-1">Track all changes and actions in your organization</p>
        </div>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-full sm:w-48 border-[#E5E5EA]" data-testid="entity-filter">
            <SelectValue placeholder="All Entities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            {entityTypes.map((type) => (
              <SelectItem key={type} value={type}>{type.replace('_', ' ').toUpperCase()}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Audit Log Table */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardHeader className="border-b border-[#E5E5EA] pb-4">
          <CardTitle className="text-heading text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#007AFF]" />
            Activity History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <FileText className="w-12 h-12 text-[#0A0A0A]/20 mb-4" />
              <p className="text-[#0A0A0A]/60">No audit logs found</p>
              <p className="text-sm text-[#0A0A0A]/40 mt-1">Actions will be logged as you use the system</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#E5E5EA]">
                  <TableHead className="text-overline text-[#0A0A0A]/50">Timestamp</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">User</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Action</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Entity</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} className="border-b border-[#E5E5EA]" data-testid={`audit-row-${log.id}`}>
                    <TableCell className="text-sm text-[#0A0A0A]/60">
                      {formatDate(log.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[#007AFF]/10 flex items-center justify-center">
                          <User className="w-3 h-3 text-[#007AFF]" />
                        </div>
                        <span className="text-sm">{log.user_email || 'System'}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getActionBadge(log.action)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {log.entity_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      {log.new_values && (
                        <div className="text-xs text-[#0A0A0A]/60 font-mono truncate">
                          {JSON.stringify(log.new_values).substring(0, 50)}...
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#F5F5F7] rounded-sm flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-[#0A0A0A]/60" />
            </div>
            <div>
              <p className="font-medium text-[#0A0A0A] mb-1">About Audit Logs</p>
              <p className="text-sm text-[#0A0A0A]/60">
                Audit logs provide a complete trail of all actions taken in your organization. 
                Every create, update, and delete operation is recorded with timestamps and user information 
                for compliance and troubleshooting purposes. Logs are immutable and cannot be modified or deleted.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
